import { app, Entity, SystemEntity } from "../ecs.ts";
import { prefabs } from "@/shared/data.ts";
import { getLocalPlayer } from "../api/player.ts";
import { getPlayer } from "@/shared/api/player.ts";
import { selection } from "../systems/selection.ts";
import { canBuild } from "../api/unit.ts";
import { updateCursor } from "../graphics/cursor.ts";
import { setFind } from "@/shared/util/set.ts";
import { computeBlueprintColor } from "../util/colorHelpers.ts";
import { queued } from "./orderHandlers.ts";
import { editorVar } from "@/vars/editor.ts";
import { BuildGrid } from "../graphics/BuildGrid.ts";
import { scene } from "../graphics/three.ts";

let blueprintIndex = 0;
let blueprint: SystemEntity<"prefab"> | undefined;
let buildGrid: BuildGrid | undefined;

export const normalize = (
  value: number,
  mode: "half" | "offset-half" | "offset-full",
) => {
  switch (mode) {
    case "half": // 0, 0.5, 1, 1.5 ...
      return Math.round(value / 0.5) * 0.5;

    case "offset-half": // 0.25, 0.75, 1.25 ...
      return (Math.round(value / 0.5 + 0.5) - 0.5) * 0.5;

    case "offset-full": // -0.5, 0.5, 1.5, ...
      return Math.round(value - 0.5) + 0.5;

    default:
      return value;
  }
};

export const normalizeBuildPosition = (
  x: number,
  y: number,
  unitType: string,
) => [
  prefabs[unitType]?.tilemap
    ? normalize(
      x,
      unitType === "tile"
        ? "offset-full"
        : ((prefabs[unitType]?.tilemap?.width ?? 0) % 4 === 0
          ? "half"
          : "offset-half"),
    )
    : x,
  prefabs[unitType]?.tilemap
    ? normalize(
      y,
      unitType === "tile"
        ? "offset-full"
        : ((prefabs[unitType]?.tilemap?.height ?? 0) % 4 === 0
          ? "half"
          : "offset-half"),
    )
    : y,
];

export const getBuilderFromBlueprint = () => {
  if (!blueprint) return;
  const unitType = blueprint.prefab;
  return setFind(
    selection,
    (u) =>
      u.actions?.some((a) => a.type === "build" && a.unitType === unitType) ??
        false,
  );
};

export const createBlueprint = (prefab: string, x: number, y: number) => {
  // Special case for ping - no builder required
  if (prefab === "ping") {
    const localPlayer = getLocalPlayer();
    if (!localPlayer) return;

    if (blueprint) app.removeEntity(blueprint);

    blueprint = app.addEntity({
      id: `blueprint-${blueprintIndex++}`,
      prefab: "ping",
      position: { x, y },
      owner: localPlayer.id,
      model: "location",
      modelScale: 1,
      isDoodad: true,
      vertexColor: 0x31aaef, // Blue color for ally
      alpha: 0.75,
    });
    updateCursor();
    return blueprint;
  }

  const builder: Entity | undefined =
    Array.from(selection).find((u) =>
      u.actions?.some((a) => a.type === "build" && a.unitType === prefab)
    ) ?? (editorVar() ? { id: "editor" } : undefined);

  if (!builder) return;

  const [normalizedX, normalizedY] = normalizeBuildPosition(x, y, prefab);

  const owner = getPlayer(builder.owner);
  const playerColor = owner?.playerColor;
  const targetColor = canBuild(builder, prefab, normalizedX, normalizedY)
    ? 0x0000ff
    : 0xff0000;

  if (blueprint) app.removeEntity(blueprint);

  if (prefabs[prefab]?.tilemap && builder.owner) {
    if (!buildGrid) {
      buildGrid = new BuildGrid();
      scene.add(buildGrid);
    }
    buildGrid.updateForBlueprint(builder, prefab, normalizedX, normalizedY);
  }

  blueprint = app.addEntity({
    id: `blueprint-${blueprintIndex++}`,
    prefab: prefab,
    position: { x: normalizedX, y: normalizedY },
    owner: owner?.id,
    model: prefabs[prefab]?.model,
    modelScale: prefabs[prefab]?.modelScale,
    isDoodad: true,
    isEffect: true,
    ...(owner &&
      {
        vertexColor: playerColor
          ? computeBlueprintColor(playerColor, targetColor)
          : targetColor,
        alpha: 0.75,
      }),
  });
  updateCursor();
  return blueprint;
};

let updateBlueprintInterval = 0;

const validateBlueprintAtPosition = (
  builder: Entity,
  prefab: string,
  x: number,
  y: number,
) => {
  const isValid = canBuild(builder, prefab, x, y);

  if (!blueprint) return;

  const localPlayer = getLocalPlayer();
  const playerColor = localPlayer?.playerColor;
  const targetColor = isValid ? 0x0000ff : 0xff0000;

  if (blueprint.owner) {
    blueprint.vertexColor = playerColor
      ? computeBlueprintColor(playerColor, targetColor)
      : targetColor;
  }
};

export const updateBlueprint = (x: number, y: number) => {
  if (!blueprint) return;

  // Special case for ping - no builder required and no normalization
  if (blueprint.prefab === "ping") {
    blueprint.position = { x, y };
    return;
  }

  const builder: Entity | undefined = getBuilderFromBlueprint() ??
    (editorVar() ? { id: "editor" } : undefined);
  if (!builder) return;

  const [normalizedX, normalizedY] = normalizeBuildPosition(
    x,
    y,
    blueprint.prefab,
  );

  blueprint.position = { x: normalizedX, y: normalizedY };

  // Update build grid if it exists
  if (buildGrid && blueprint.owner) {
    buildGrid.updateForBlueprint(
      builder,
      blueprint.prefab,
      normalizedX,
      normalizedY,
    );
  }

  // Start interval to revalidate pathing when units move through the area
  if (!updateBlueprintInterval) {
    updateBlueprintInterval = setInterval(() => {
      if (!blueprint?.position) {
        clearInterval(updateBlueprintInterval);
        updateBlueprintInterval = 0;
        return;
      }

      updateBlueprint(blueprint.position.x, blueprint.position.y);
    }, 250);
  }

  // Fast validation - no debouncing needed
  validateBlueprintAtPosition(
    builder,
    blueprint.prefab,
    normalizedX,
    normalizedY,
  );
};

export const cancelBlueprint = () => {
  queued.state = false;
  if (blueprint) {
    app.removeEntity(blueprint);
    blueprint = undefined;
    updateCursor();
  }
  if (buildGrid) buildGrid.hide();
  if (updateBlueprintInterval) {
    clearInterval(updateBlueprintInterval);
    updateBlueprintInterval = 0;
  }
};

export const clearBlueprint = (
  fn?: (blueprint: SystemEntity<"prefab">) => void,
) => {
  if (blueprint && (!fn || fn(blueprint))) cancelBlueprint();
};

export const hasBlueprint = () => !!blueprint;
export const getBlueprint = () => blueprint;
export const getBlueprintPrefab = () => blueprint?.prefab;
